import { adminAllHostPoolsQueryAtom } from "@/admin/host_pools/hostPoolsQuery.ts";
import { metalSpecQueryAtom } from "@/admin/metal/specs/specQuery.ts";
import type { OptionalElement } from "@/components/SeparatedBy.tsx";
import { configAtom } from "@/config.ts";
import { CountryFlag } from "@/modules/location/CountryFlag.tsx";
import { queryAtom, type LocationSlug } from "@/modules/location/locationQuery.ts";
import { DEFAULT_KEY, getHostPoolKey, getMetalSpecKey, type PricePoolKey } from "@/modules/pricing/resourcePricing.ts";
import { constantQueryResult, mergeLoadedQueries } from "@/utils/query/mergeQueries.ts";
import { mapFrom } from "@warrenio/utils/collections/maps";
import { mustGet } from "@warrenio/utils/collections/mustGet";
import { atom } from "jotai/vanilla";
import type { ReactNode } from "react";

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
export type LocKey = typeof DEFAULT_KEY | LocationSlug | PricePoolKey;
export type LocKeyType = "default" | "location" | "host_pool" | "metal_spec";

/** Information about a selectable "location" (pool) for which pricing can be added */
export interface PriceLoc {
    key: LocKey;
    fallbackKeys: LocKey[];
    type: LocKeyType;

    title: OptionalElement;
    icon: ReactNode;
}

/** Return the entire sequence of keys that will be searched for a price (eg. for host pools: `[host_pool_key, location_key, DEFAULT_KEY]`) */
export function getAllLocKeys(loc: PriceLoc): LocKey[] {
    return [loc.key, ...loc.fallbackKeys, DEFAULT_KEY];
}

const DEFAULT_LOC: PriceLoc = {
    key: DEFAULT_KEY,
    fallbackKeys: [],
    type: "default",
    title: "DEFAULT",
    icon: null,
};

const specsIfEnabledQueryAtom = atom((get) => {
    const { bareMetalEnabled } = get(configAtom);
    return bareMetalEnabled ? get(metalSpecQueryAtom) : constantQueryResult(null);
});

export const priceLocsAtom = atom((get) => {
    const locations = get(queryAtom);
    const pools = get(adminAllHostPoolsQueryAtom);
    const specs = get(specsIfEnabledQueryAtom);

    return mergeLoadedQueries({ locations, pools, specs }, ({ locations, pools, specs }) => {
        const locMap = mapFrom(locations, (l) => l.slug);

        const result: PriceLoc[] = [DEFAULT_LOC];

        for (const loc of locations) {
            result.push({
                key: loc.slug,
                fallbackKeys: [],
                type: "location",
                title: loc.display_name,
                icon: <CountryFlag code={loc.country_code} />,
            });
        }

        for (const pool of pools.values()) {
            const loc = mustGet(locMap, pool.location);
            result.push({
                key: getHostPoolKey(pool.uuid),
                fallbackKeys: [loc.slug],
                type: "host_pool",
                title: `${loc.display_name} - ${pool.name}`,
                icon: <CountryFlag code={loc.country_code} />,
            });
        }

        for (const spec of specs?.values() ?? []) {
            const loc = mustGet(locMap, spec.location);
            result.push({
                key: getMetalSpecKey(spec.uuid),
                fallbackKeys: [loc.slug],
                type: "metal_spec",
                title: `${loc.display_name} - ${spec.title}`,
                icon: <CountryFlag code={loc.country_code} />,
            });
        }

        return result;
    });
});
